/**
 * Route Configuration
 * 
 * Centralized configuration for route-specific behavior in the header and other components.
 * This makes it easy to add new routes without modifying multiple conditional checks.
 * 
 * To add a new route:
 * 1. Add the route path pattern to the routes object
 * 2. Configure the features/behaviors needed for that route
 * 3. The header will automatically adapt based on this configuration
 */

export const ROUTE_CONFIG = {
  // Plan Version/Edit Plan Page
  PLAN_VERSION: {
    pathPattern: '/planVersion',
    title: null, // Dynamic title from planDetails
    features: {
      showPlanSelector: true,
      showBreadcrumbs: true,
      showSaveButtons: true,
      showDiscardButton: true,
      showSaveAsNewButton: true,
      showUserAvatars: true,
      showCompareButton: true,
      showImportActualButton: true,
      showMarkCompleteButton: true,
      showProfile: false, // Hide profile on plan version page
      fetchHeaderData: true,
      showLoadingSkeleton: true,
    },
    pageType: 'planVersion',
  },

  // Chart of Accounts Management Page
  COA_MANAGEMENT: {
    pathPattern: '/ChartOfAccounts',
    title: 'Chart of account management',
    features: {
      showPlanSelector: false,
      showBreadcrumbs: false,
      showSaveButtons: true,
      showDiscardButton: true,
      showSaveAsNewButton: false, // COA doesn't have save as new
      showUserAvatars: false, // COA page doesn't show user avatars
      showCompareButton: false,
      showImportActualButton: false,
      showMarkCompleteButton: false,
      showProfile: true,
      fetchHeaderData: false,
      showLoadingSkeleton: false,
    },
    pageType: 'coa',
  },

  // Settings Page
  SETTINGS: {
    pathPattern: '/settings',
    title: 'Settings',
    features: {
      showPlanSelector: false,
      showBreadcrumbs: false,
      showSaveButtons: false,
      showDiscardButton: false,
      showSaveAsNewButton: false,
      showUserAvatars: false,
      showCompareButton: false,
      showImportActualButton: false,
      showMarkCompleteButton: false,
      showProfile: true,
      fetchHeaderData: false,
      showLoadingSkeleton: false,
    },
    pageType: 'settings',
  },

  // Exports Page
  TPX_REPORTS: {
    pathPattern: '/exports',
    title: 'Exports',
    features: {
      showPlanSelector: false,
      showBreadcrumbs: false,
      showSaveButtons: false,
      showDiscardButton: false,
      showSaveAsNewButton: false,
      showUserAvatars: false,
      showCompareButton: false,
      showImportActualButton: false,
      showMarkCompleteButton: false,
      showProfile: true,
      fetchHeaderData: false,
      showLoadingSkeleton: false,
    },
    pageType: 'reports',
  },

  // Data migration (TPL / SnapLogic, migration jobs)
  DATA_MIGRATION: {
    pathPattern: '/data-migration',
    title: 'Data migration',
    features: {
      showPlanSelector: false,
      showBreadcrumbs: false,
      showSaveButtons: false,
      showDiscardButton: false,
      showSaveAsNewButton: false,
      showUserAvatars: false,
      showCompareButton: false,
      showImportActualButton: false,
      showMarkCompleteButton: false,
      showProfile: true,
      fetchHeaderData: false,
      showLoadingSkeleton: false,
    },
    pageType: 'dataMigration',
  },

  // Paired-daily dedup correction utility
  PAIRED_DAILY_DEDUP: {
    pathPattern: '/paired-daily-dedup',
    title: 'Paired-daily dedup',
    features: {
      showPlanSelector: false,
      showBreadcrumbs: false,
      showSaveButtons: false,
      showDiscardButton: false,
      showSaveAsNewButton: false,
      showUserAvatars: false,
      showCompareButton: false,
      showImportActualButton: false,
      showMarkCompleteButton: false,
      showProfile: true,
      fetchHeaderData: false,
      showLoadingSkeleton: false,
    },
    pageType: 'dataMigration',
  },

  // Critique Template Edit Page (more specific, must come before CRITIQUE)
  CRITIQUE_TEMPLATE_EDIT: {
    pathPattern: '/critique/template',
    title: null, // Dynamic title from templateData
    features: {
      showPlanSelector: false,
      showBreadcrumbs: true,
      showSaveButtons: true,
      showDiscardButton: false,
      showSaveAsNewButton: false,
      showUserAvatars: false,
      showCompareButton: false,
      showImportActualButton: false,
      showMarkCompleteButton: false,
      showProfile: false, // Hide profile on edit page (like planVersion)
      fetchHeaderData: true, // Fetch template data
      showLoadingSkeleton: true,
    },
    pageType: 'critiqueTemplateEdit',
  },

  // Critique Details/Edit Page (single critique instance)
  CRITIQUE_DETAILS: {
    pathPattern: '/critique/details',
    title: null,
    features: {
      showPlanSelector: false,
      showBreadcrumbs: true,
      showSaveButtons: true,
      showDiscardButton: false,
      showSaveAsNewButton: false,
      showUserAvatars: true,
      showCompareButton: false,
      showImportActualButton: false,
      showMarkCompleteButton: false,
      showProfile: true,
      fetchHeaderData: true,
      showLoadingSkeleton: true,
    },
    pageType: 'critiqueDetails',
  },

  // All Critiques Page
  ALL_CRITIQUES: {
    pathPattern: '/critique/all',
    title: 'All critiques',
    features: {
      showPlanSelector: false,
      showBreadcrumbs: false,
      showSaveButtons: false,
      showDiscardButton: false,
      showSaveAsNewButton: false,
      showUserAvatars: false,
      showCompareButton: false,
      showImportActualButton: false,
      showMarkCompleteButton: false,
      showProfile: true,
      fetchHeaderData: false,
      showLoadingSkeleton: false,
    },
    pageType: 'allCritiques',
  },

  // Critique Page
  CRITIQUE: {
    pathPattern: '/critique',
    title: 'Critique templates',
    features: {
      showPlanSelector: false,
      showBreadcrumbs: false,
      showSaveButtons: false,
      showDiscardButton: false,
      showSaveAsNewButton: false,
      showUserAvatars: false,
      showCompareButton: false,
      showImportActualButton: false,
      showMarkCompleteButton: false,
      showProfile: true,
      fetchHeaderData: false,
      showLoadingSkeleton: false,
    },
    pageType: 'critique',
  },

  // All Plans/Home Page (default)
  ALL_PLANS: {
    pathPattern: '/',
    title: 'All plans',
    features: {
      showPlanSelector: false,
      showBreadcrumbs: false,
      showSaveButtons: false,
      showDiscardButton: false,
      showSaveAsNewButton: false,
      showUserAvatars: false,
      showCompareButton: false,
      showImportActualButton: false,
      showMarkCompleteButton: false,
      showProfile: true,
      fetchHeaderData: false,
      showLoadingSkeleton: false,
    },
    pageType: 'allPlans',
  },
};

/**
 * Get route configuration based on current pathname
 * @param {string} pathname - Current route pathname
 * @returns {Object} Route configuration object
 */
export const getRouteConfig = (pathname) => {
  // Check routes in order of specificity (most specific first)
  // This allows for nested routes or more specific patterns
  
  // Sort routes by pathPattern length (longer = more specific) to check most specific first
  const sortedRoutes = Object.entries(ROUTE_CONFIG).sort((a, b) => {
    const aLength = a[1].pathPattern.length;
    const bLength = b[1].pathPattern.length;
    return bLength - aLength; // Descending order (longest first)
  });
  
  // Check each route configuration (most specific first)
  for (const [key, config] of sortedRoutes) {
    if (config.pathPattern === '/') {
      // Special handling for root path
      if (pathname === '/' || pathname === '') {
        return config;
      }
    } else {
      // Check exact match first
      if (pathname === config.pathPattern) {
        return config;
      }
      // For multi-segment paths like '/critique/template', check if pathname starts with pattern + '/'
      // This ensures /critique/template/123 matches /critique/template but /critique/templates doesn't
      if (config.pathPattern.includes('/') && pathname.startsWith(config.pathPattern + '/')) {
        return config;
      }
      // For single-segment paths like '/settings', '/exports', '/ChartOfAccounts', '/critique'
      // Check if pathname starts with pattern + '/' or equals pattern
      if (!config.pathPattern.includes('/') && (pathname === config.pathPattern || pathname.startsWith(config.pathPattern + '/'))) {
        return config;
      }
    }
  }
  
  // Default to ALL_PLANS if no match
  return ROUTE_CONFIG.ALL_PLANS;
};

/**
 * Check if a specific feature is enabled for the current route
 * @param {string} pathname - Current route pathname
 * @param {string} feature - Feature name to check
 * @returns {boolean} Whether the feature is enabled
 */
export const isFeatureEnabled = (pathname, feature) => {
  const config = getRouteConfig(pathname);
  return config.features[feature] === true;
};

/**
 * Get the page type for the current route
 * @param {string} pathname - Current route pathname
 * @returns {string} Page type identifier
 */
export const getPageType = (pathname) => {
  const config = getRouteConfig(pathname);
  return config.pageType;
};

/**
 * Get the title for the current route
 * @param {string} pathname - Current route pathname
 * @returns {string|null} Page title (null if dynamic)
 */
export const getPageTitle = (pathname) => {
  const config = getRouteConfig(pathname);
  return config.title;
};

/**
 * Check if a pathname matches a specific route pattern
 * @param {string} pathname - Current route pathname
 * @param {string} routePathPattern - Route path pattern to match against
 * @returns {boolean} Whether the pathname matches the route
 */
export const matchesRoute = (pathname, routePathPattern) => {
  if (routePathPattern === '/') {
    return pathname === '/' || pathname === '';
  }
  return pathname.includes(routePathPattern);
};

/**
 * Check if a pathname exactly matches a route (for exact menu item matching)
 * @param {string} pathname - Current route pathname
 * @param {string} routePathPattern - Route path pattern to match against
 * @returns {boolean} Whether the pathname exactly matches the route
 */
export const exactMatchRoute = (pathname, routePathPattern) => {
  if (routePathPattern === '/') {
    return pathname === '/' || pathname === '';
  }
  return pathname === routePathPattern || pathname.startsWith(routePathPattern + '/');
};

